import type { ComponentChildren, JSX, TargetedKeyboardEvent } from "preact";
import { useEffect, useRef, useState } from "preact/hooks";
import type { NavProps } from "./Nav";
import { Nav } from "./Nav";
import { NavHr } from "./NavHr";
import { NavItem } from "./NavItem";
import { NavLink } from "./NavLink";

export interface NavMenuClickNode {
    onClick: () => void;
    className?: string;
    children: ComponentChildren;
}

export interface NavMenuPathNode {
    path: string;
    absolute?: boolean;
    className?: string;
    children: ComponentChildren;
}

interface NavMenuHrNode {
    hr: true;
}

export type NavMenuNode = NavMenuClickNode | NavMenuPathNode | NavMenuHrNode;

interface Props extends NavProps {
    activeNode: NavMenuNode | undefined;
    children: NavMenuNode[];
}

export function NavMenu({
    activeNode,
    children,
    ...rest
}: Props): JSX.Element | null {
    const [selectedNode, setSelectedNode] = useState<NavMenuNode | undefined>(
        activeNode ?? children[0],
    );
    const ref = useRef<HTMLUListElement>(null);

    useEffect(() => {
        if (activeNode != null) {
            setSelectedNode(activeNode);
        }
    }, [activeNode]);

    if (selectedNode == null) {
        return null;
    }

    const navigate = (index: number, diff: number) => {
        if (ref.current == null || index < 0 || index >= children.length) {
            return;
        }
        const node = children[index];
        if (node !== selectedNode) {
            if ("hr" in node) {
                navigate(index + diff, diff);
                return;
            }
            setSelectedNode(node);
            const el = ref.current.children[index].firstChild;
            if (el instanceof HTMLElement) {
                el.focus();
            }
        }
    };

    const onKeyDown = (e: TargetedKeyboardEvent<Element>) => {
        switch (e.key) {
            case "ArrowLeft":
            case "ArrowUp":
                navigate(children.indexOf(selectedNode) - 1, -1);
                break;
            case "ArrowRight":
            case "ArrowDown":
                navigate(children.indexOf(selectedNode) + 1, 1);
                break;
            case "Home":
                navigate(0, 1);
                break;
            case "End":
                navigate(children.length - 1, -1);
                break;
            case "Enter":
            case " ":
                if (ref.current) {
                    const index = children.indexOf(selectedNode);
                    const el = ref.current.children[index].firstChild;
                    if (el instanceof HTMLElement) {
                        el.click();
                    }
                }
                break;
            default:
                // Important so we don't run preventDefault() for unknown keys
                return;
        }
        e.preventDefault();
    };

    const isTab = rest.variant !== "navbar";

    return (
        <Nav {...rest} onKeyDown={onKeyDown} ref={ref}>
            {children.map((node, index) => {
                if ("hr" in node) {
                    return <NavHr key={index} />;
                }
                const isActive = activeNode === node;
                const isSelected = selectedNode === node;
                return (
                    <NavItem key={index} aria-selected={isActive}>
                        <NavLink
                            data-nav-tabs-index={index}
                            active={isActive}
                            tabIndex={isSelected ? 0 : -1}
                            isTab={isTab}
                            className={node.className}
                            absolute={"absolute" in node && node.absolute}
                            href={"path" in node ? node.path : undefined}
                            onClick={
                                "onClick" in node ? node.onClick : undefined
                            }
                        >
                            {node.children}
                        </NavLink>
                    </NavItem>
                );
            })}
        </Nav>
    );
}
